import { Flex, Stack, Center, Loader, Alert, Paper, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconFolderOff } from '@tabler/icons-react';
import { useIntl } from 'react-intl';
import { myFetch } from '@/commons/fetch';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearFolders,
  replaceFoldersFromQuery,
} from '@store/slices/foldersSlice';
import { selectAllFolders } from '@store/selectors/foldersSelectors';
import { FolderCard, PaginationControls, FilterControls } from './components';
import { Folder } from './types';
import { FilterField } from '@/users/pages/UserFilters/types/FilterField';
import {
  parseQueryFoldersSearchParams,
  serializeQueryFoldersSearchParams,
} from './utils/queryFoldersUrlParams';

type QueryFoldersApiResponse = {
  success: boolean;
  data: Folder[];
  totalItems: number;
  totalPages: number;
  page: number;
  itemsPerPage: number;
};

const FILTER_QUERY_DEBOUNCE_MS = 500;

function queryFoldersUrlStateSig(
  filters: Record<string, unknown>,
  page: number,
  ipp: number
): string {
  return JSON.stringify({ f: filters, p: page, i: ipp });
}

function queryFoldersFiltersSig(filters: Record<string, unknown>): string {
  return JSON.stringify(filters);
}

/** Stable fallback when `filter.fields` is missing (avoids new `[]` each render). */
const NO_CUSTOM_FIELDS: FilterField[] = [];

function emptyQueryFilters(fields: FilterField[] | undefined) {
  const next: Record<string, unknown> = {
    isArchived: false,
    onlyWithData: false,
    backOfficeStatus: '',
    folderName: '',
    fileName: '',
    fileType: '',
    isPublished: '',
    isSigned: '',
    isLocked: '',
    fileStatus: '',
  };
  for (const field of fields ?? []) {
    next[field.name] = '';
    next[`${field.name}_sort`] = '';
  }
  return next;
}

function initialQueryFoldersFromSearchParams(
  searchParams: URLSearchParams,
  filterFields: FilterField[] | undefined
) {
  const empty = emptyQueryFilters(filterFields);
  return parseQueryFoldersSearchParams(
    new URLSearchParams(searchParams.toString()),
    empty
  );
}

type FetchParams = {
  filters: Record<string, unknown>;
  page: number;
  itemsPerPage: number;
};

function QueryFoldersImpl({ filter }: { filter: any }) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const folders = useAppSelector(selectAllFolders);
  const [searchParams, setSearchParams] = useSearchParams();

  const fieldsKey = useMemo(
    () =>
      (filter?.fields ?? NO_CUSTOM_FIELDS)
        .map((f: FilterField) => `${f.name}:${f.path}:${f.type}`)
        .join('|'),
    [filter?.fields]
  );

  const fields: FilterField[] = useMemo(
    () => filter?.fields ?? NO_CUSTOM_FIELDS,
    [filter?.fields, fieldsKey]
  );

  const filterForFetch = useMemo(
    () => ({
      id: filter?.id,
      category: filter?.category,
      fields,
    }),
    [filter?.id, filter?.category, fields]
  );

  const emptyFilters = useMemo(
    () => emptyQueryFilters(filter?.fields),
    [filter?.id, fieldsKey]
  );

  const searchParamsKey = searchParams.toString();

  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersState, setFiltersState] = useState(
    () =>
      initialQueryFoldersFromSearchParams(searchParams, filter?.fields).filters
  );
  const [debouncedFiltersState] = useDebouncedValue(
    filtersState,
    FILTER_QUERY_DEBOUNCE_MS
  );
  const [currentPage, setCurrentPage] = useState(
    () => initialQueryFoldersFromSearchParams(searchParams, filter?.fields).page
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    () =>
      initialQueryFoldersFromSearchParams(searchParams, filter?.fields)
        .itemsPerPage
  );

  const [urlHydrated, setUrlHydrated] = useState(false);
  const [skipUrlParseOnce, setSkipUrlParseOnce] = useState(false);
  const [suppressSyncFetchOnce, setSuppressSyncFetchOnce] = useState(false);
  const [suppressPaginationFetchOnce, setSuppressPaginationFetchOnce] =
    useState(false);
  const [redundantDebouncedSkipSig, setRedundantDebouncedSkipSig] = useState<
    string | null
  >(null);
  const [isFirstDebouncedCommit, setIsFirstDebouncedCommit] = useState(true);
  const [inFlightAbort, setInFlightAbort] = useState<AbortController | null>(
    null
  );

  const runFetch = useCallback(
    async ({ filters, page, itemsPerPage: ipp }: FetchParams) => {
      if (!filterForFetch.id) {
        setIsLoading(false);
        return;
      }

      const ac = new AbortController();
      setInFlightAbort((prev) => {
        prev?.abort();
        return ac;
      });

      dispatch(clearFolders());
      setIsLoading(true);
      setError(null);

      const requestedPage = Math.max(1, Number(page) || 1);
      const perPage = Math.max(1, Number(ipp) || 10);

      try {
        const body = {
          category: filterForFetch.category || undefined,
          fields: filterForFetch.fields,
          filters,
          page: requestedPage,
          itemsPerPage: perPage,
        };

        const res = (await myFetch('/folders/query', {
          method: 'POST',
          body: JSON.stringify(body),
          signal: ac.signal,
        })) as QueryFoldersApiResponse;

        if (ac.signal.aborted) {
          return;
        }

        dispatch(replaceFoldersFromQuery(res.data || []));
        setTotalItems(res.totalItems ?? 0);
        setTotalPages(res.totalPages ?? 1);
        if (res.page != null && res.page !== requestedPage) {
          setCurrentPage(res.page);
        }
      } catch (e: any) {
        if (ac.signal.aborted) {
          return;
        }
        setError(e?.message || 'Request failed');
        dispatch(clearFolders());
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        if (!ac.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [dispatch, filterForFetch]
  );

  useLayoutEffect(() => {
    if (!filter?.id) {
      setUrlHydrated(true);
      return;
    }
    if (skipUrlParseOnce) {
      setSkipUrlParseOnce(false);
      setUrlHydrated(true);
      return;
    }

    const parsed = parseQueryFoldersSearchParams(
      new URLSearchParams(searchParams.toString()),
      emptyFilters
    );
    const nextSig = queryFoldersUrlStateSig(
      parsed.filters,
      parsed.page,
      parsed.itemsPerPage
    );
    const curSig = queryFoldersUrlStateSig(
      filtersState,
      currentPage,
      itemsPerPage
    );
    if (nextSig === curSig) {
      setUrlHydrated(true);
      return;
    }

    setRedundantDebouncedSkipSig(queryFoldersFiltersSig(parsed.filters));
    setSuppressSyncFetchOnce(true);
    setFiltersState(parsed.filters);
    setCurrentPage(parsed.page);
    setItemsPerPage(parsed.itemsPerPage);
    void runFetch({
      filters: parsed.filters,
      page: parsed.page,
      itemsPerPage: parsed.itemsPerPage,
    });
    setUrlHydrated(true);
    // Intentionally react only to URL / filter identity; read latest filters + pagination from this commit without subscribing (same idea as prior refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParamsKey drives external URL sync
  }, [searchParamsKey, filter?.id, runFetch]);

  /** When custom fields load, extend filter state with new default keys only (same URL → no refetch). */
  useLayoutEffect(() => {
    if (!filter?.id) {
      return;
    }
    const next = { ...filtersState };
    let changed = false;
    for (const key of Object.keys(emptyFilters)) {
      if (!(key in next)) {
        next[key] = emptyFilters[key];
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    setRedundantDebouncedSkipSig(queryFoldersFiltersSig(next));
    setSuppressSyncFetchOnce(true);
    setFiltersState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when empty schema changes, not on every filter keystroke
  }, [emptyFilters, filter?.id]);

  useEffect(() => {
    if (!filter?.id || !urlHydrated) {
      return;
    }
    const next = serializeQueryFoldersSearchParams(
      debouncedFiltersState,
      currentPage,
      itemsPerPage,
      emptyFilters
    );
    if (next.toString() === searchParams.toString()) {
      return;
    }
    setSkipUrlParseOnce(true);
    setSearchParams(next, { replace: true });
  }, [
    filter?.id,
    debouncedFiltersState,
    currentPage,
    itemsPerPage,
    emptyFilters,
    searchParams,
    setSearchParams,
    urlHydrated,
  ]);

  useEffect(() => {
    if (!urlHydrated) {
      return;
    }
    if (suppressSyncFetchOnce) {
      setSuppressSyncFetchOnce(false);
      return;
    }
    if (suppressPaginationFetchOnce) {
      setSuppressPaginationFetchOnce(false);
      return;
    }
    void runFetch({
      filters: filtersState,
      page: currentPage,
      itemsPerPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- suppress* flags are read once per dep change (ref-like); omit filtersState to avoid fetch on every keystroke
  }, [currentPage, itemsPerPage, filter?.id, urlHydrated, runFetch]);

  useEffect(() => {
    if (isFirstDebouncedCommit) {
      setIsFirstDebouncedCommit(false);
      setRedundantDebouncedSkipSig(null);
      return;
    }
    if (redundantDebouncedSkipSig !== null) {
      if (
        queryFoldersFiltersSig(debouncedFiltersState) ===
        redundantDebouncedSkipSig
      ) {
        setRedundantDebouncedSkipSig(null);
        return;
      }
      setRedundantDebouncedSkipSig(null);
    }
    void runFetch({
      filters: debouncedFiltersState,
      page: currentPage,
      itemsPerPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- first commit + skip-sig mirror prior ref semantics
  }, [debouncedFiltersState, currentPage, itemsPerPage, runFetch]);

  useEffect(() => {
    if (!isLoading && totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, isLoading]);

  useEffect(() => {
    return () => {
      inFlightAbort?.abort();
    };
  }, [inFlightAbort]);

  useEffect(
    () => () => {
      dispatch(clearFolders());
    },
    [dispatch]
  );

  const resetPageForFilterChange = useCallback(() => {
    setCurrentPage((p) => {
      if (p !== 1) {
        setSuppressPaginationFetchOnce(true);
      }
      return 1;
    });
  }, []);

  const refetchAfterMutation = useCallback(() => {
    void runFetch({
      filters: filtersState,
      page: currentPage,
      itemsPerPage,
    });
  }, [runFetch, filtersState, currentPage, itemsPerPage]);

  const paginationProps = useMemo(
    () => ({
      totalItems,
      currentPage,
      itemsPerPage,
      totalPages,
      setCurrentPage,
      setItemsPerPage,
    }),
    [totalItems, currentPage, itemsPerPage, totalPages]
  );

  if (!filter?.id) {
    return null;
  }

  return (
    <Stack gap="md" my="md">
      {error ? (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      ) : null}

      <FilterControls
        filters={filtersState as any}
        customFieldBasedFilters={fields}
        setFilters={setFiltersState}
        resetPage={resetPageForFilterChange}
        hasIsPublishedFiles
        hasIsSignedFiles
        hasIsLockedFiles
        hasFileStatus
      />

      {!isLoading ? <PaginationControls {...paginationProps} /> : null}

      {isLoading ? (
        <Center h={250}>
          <Loader />
        </Center>
      ) : error ? null : totalItems === 0 ? (
        <Paper p="xl" radius="md" withBorder>
          <Center py="md">
            <Stack align="center" gap="md" maw={440} ta="center">
              <IconFolderOff size={52} stroke={1.25} color="gray" aria-hidden />
              <Text fw={600} size="lg">
                {intl.formatMessage({ id: 'queryFoldersNoResultsTitle' })}
              </Text>
              <Text c="dimmed" size="sm">
                {intl.formatMessage({ id: 'queryFoldersNoResultsDescription' })}
              </Text>
            </Stack>
          </Center>
        </Paper>
      ) : (
        <Flex direction="column" gap="md">
          {folders.map((folder) => (
            <FolderCard
              key={folder.id}
              folder={folder}
              customFieldBasedFilters={fields}
              showAllCustomFields
              onFolderMutationComplete={refetchAfterMutation}
            />
          ))}
        </Flex>
      )}

      {!isLoading ? <PaginationControls {...paginationProps} /> : null}
    </Stack>
  );
}

export const QueryFolders = ({ filter }: { filter?: any }) => {
  if (!filter?.id) {
    return null;
  }
  return <QueryFoldersImpl key={String(filter.id)} filter={filter} />;
};
